import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, ClipboardList, Send, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { API_BASE as API } from '../lib/apiBase';

export default function DirectorDepartmentsPage() {
  const { user, token, isDirector, loading: authLoading } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const isEs = lang === 'es';

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  const [moduleOk, setModuleOk] = useState(false);
  const [list, setList] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [steps, setSteps] = useState('');
  const [expected, setExpected] = useState('');
  const [actual, setActual] = useState('');
  const [environment, setEnvironment] = useState('');
  const [improve, setImprove] = useState('');
  const [severity, setSeverity] = useState('MEDIUM');
  const [sendNote, setSendNote] = useState({});

  const load = useCallback(async () => {
    if (!token || !isDirector) return;
    setLoadErr('');
    try {
      const en = await fetch(`${API}/api/director/departments/enabled`, { headers }).then(r => r.json());
      setModuleOk(Boolean(en?.enabled));
      const r = await fetch(`${API}/api/director/departments/contracts?limit=100`, { headers });
      if (r.status === 403) {
        setLoadErr(isEs ? 'Solo la directora puede ver esta area.' : 'Director access only.');
        return;
      }
      const d = await r.json();
      setList(d.contracts || []);
    } catch (e) {
      setLoadErr(isEs ? 'No se pudo cargar.' : 'Could not load.');
    }
  }, [token, isDirector, headers, isEs]);

  useEffect(() => {
    if (authLoading) return;
    if (isDirector) load();
  }, [authLoading, isDirector, load]);

  const submitReport = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    const stepsArr = steps.split('\n').map((s) => s.trim()).filter(Boolean);
    try {
      const r = await fetch(`${API}/api/director/departments/qa/reports`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          title: title.trim(),
          steps_to_reproduce: stepsArr,
          expected: expected.trim(),
          actual: actual.trim(),
          environment: environment.trim(),
          improvement_ideas: improve.trim(),
          lang: isEs ? 'es' : 'en',
          severity: severity || 'MEDIUM',
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || r.status);
      }
      setTitle('');
      setSteps('');
      setExpected('');
      setActual('');
      setEnvironment('');
      setImprove('');
      setSeverity('MEDIUM');
      await load();
    } catch (err) {
      setLoadErr(String(err.message || err));
    }
    setSaving(false);
  };

  const sendToTech = async (reportId) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/director/departments/qa/reports/${reportId}/send-to-tech`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ note: (sendNote[reportId] || '').trim() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail));
      }
      setSendNote((n) => ({ ...n, [reportId]: '' }));
      await load();
    } catch (err) {
      setLoadErr(String(err.message || err));
    }
    setSaving(false);
  };

  const patchTask = async (taskId, status) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/director/departments/tech/tasks/${taskId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status, note: '' }),
      });
      if (!r.ok) throw new Error('patch');
      await load();
    } catch {
      setLoadErr(isEs ? 'No se pudo actualizar la tarea.' : 'Could not update task.');
    }
    setSaving(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-pulse-bg flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isDirector) {
    return (
      <div className="min-h-screen bg-pulse-bg flex flex-col items-center justify-center p-6 text-center">
        <p className="text-pulse-muted mb-4 max-w-md">
          {isEs ? 'Esta area es solo para la directora de la plataforma.' : 'This area is for the platform director only.'}
        </p>
        <Link to="/dashboard" className="text-cyan-400 hover:underline">{isEs ? 'Volver al panel' : 'Back to dashboard'}</Link>
      </div>
    );
  }

  const reports = list.filter((c) => c.type === 'QA_REPORT');
  const tasks = list.filter((c) => c.type === 'TECH_TASK');

  return (
    <div className="min-h-screen bg-pulse-bg text-white">
      <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
        <div className="flex items-center gap-3 mb-8">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-xl border border-pulse-border hover:bg-pulse-elevated"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-cyan-400/90 font-bold flex items-center gap-1.5">
              <Building2 size={12} /> {isEs ? 'Direccion' : 'Leadership'}
            </p>
            <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
              <Sparkles className="text-amber-400" size={24} />
              {isEs ? 'Departamentos internos' : 'Internal departments'}
            </h1>
            <p className="text-sm text-pulse-muted mt-1 max-w-xl">
              {isEs
                ? 'Solo tu. Aqui el equipo de calidad (QA) deja informes estructurados. Puedes enviarlos a Tecnico como tarea sin tocar codigo ni dinero aqui.'
                : 'Director only. QA-style reports and handoff to technical tasks—no code or money changes in this screen.'}
            </p>
          </div>
        </div>

        {!moduleOk && (
          <div className="mb-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-200 text-sm">
            {isEs ? 'Modulo desactivado en ajustes del sistema.' : 'This module is disabled in system settings.'}
          </div>
        )}

        {loadErr && (
          <div className="mb-6 p-4 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-sm">{loadErr}</div>
        )}

        <section className="mb-10 rounded-2xl border border-pulse-border bg-pulse-surface/80 p-6">
          <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
            <ClipboardList size={18} className="text-amber-400" />
            {isEs ? 'Nuevo informe de calidad (QA)' : 'New quality report (QA)'}
          </h2>
          <form onSubmit={submitReport} className="space-y-4">
            <div>
              <label className="text-xs text-pulse-muted block mb-1">{isEs ? 'Titulo' : 'Title'}</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-2.5 text-sm"
                required
                placeholder={isEs ? 'Que probaste' : 'What you tested'}
              />
            </div>
            <div>
              <label className="text-xs text-pulse-muted block mb-1">{isEs ? 'Pasos (uno por linea)' : 'Steps (one per line)'}</label>
              <textarea
                value={steps}
                onChange={(e) => setSteps(e.target.value)}
                rows={4}
                className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-2.5 text-sm font-mono text-xs"
                placeholder="1) ...\n2) ..."
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-pulse-muted block mb-1">{isEs ? 'Esperado' : 'Expected'}</label>
                <textarea value={expected} onChange={(e) => setExpected(e.target.value)} rows={3} className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-2.5 text-sm" />
              </div>
              <div>
                <label className="text-xs text-pulse-muted block mb-1">{isEs ? 'Lo que ocurrio' : 'What happened'}</label>
                <textarea value={actual} onChange={(e) => setActual(e.target.value)} rows={3} className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-2.5 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs text-pulse-muted block mb-1">{isEs ? 'Entorno (navegador, movil, etc.)' : 'Environment'}</label>
              <input value={environment} onChange={(e) => setEnvironment(e.target.value)} className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-2.5 text-sm" />
            </div>
            <div>
              <label className="text-xs text-pulse-muted block mb-1">{isEs ? 'Gravedad' : 'Severity'}</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full max-w-xs bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-2.5 text-sm"
              >
                {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-pulse-muted block mb-1">{isEs ? 'Como lo mejorarias' : 'How you would improve it'}</label>
              <textarea value={improve} onChange={(e) => setImprove(e.target.value)} rows={2} className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-2.5 text-sm" />
            </div>
            <button
              type="submit"
              disabled={saving || !moduleOk}
              className="bg-amber-500 text-black font-bold px-6 py-2.5 rounded-xl disabled:opacity-50"
            >
              {saving ? '...' : isEs ? 'Guardar informe' : 'Save report'}
            </button>
          </form>
        </section>

        <section className="mb-10">
          <h2 className="font-heading font-bold text-lg mb-3">{isEs ? 'Bandeja: informes QA' : 'Inbox: QA reports'}</h2>
          <ul className="space-y-3">
            {reports.length === 0 && <li className="text-pulse-muted text-sm">{isEs ? 'Ningun informe aun.' : 'No reports yet.'}</li>}
            {reports.map((r) => {
              const p = r.payload || {};
              return (
                <li key={r.id} className="rounded-2xl border border-pulse-border bg-pulse-surface/50 p-4 text-sm">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <span className="text-amber-400/90 font-mono text-xs">{r.public_code}</span>
                      <span className="ml-2 text-pulse-muted">· {r.status}</span>
                      <h3 className="font-bold text-base mt-1">{p.title}</h3>
                    </div>
                    {r.status === 'OPEN' && (
                      <div className="flex flex-col gap-2 w-full md:w-auto md:min-w-[200px]">
                        <input
                          type="text"
                          className="bg-pulse-elevated border border-pulse-border rounded-lg px-2 py-1.5 text-xs"
                          placeholder={isEs ? 'Nota breve al enviar' : 'Short note when sending'}
                          value={sendNote[r.id] || ''}
                          onChange={(e) => setSendNote((n) => ({ ...n, [r.id]: e.target.value }))}
                        />
                        <button
                          type="button"
                          disabled={saving || !moduleOk}
                          onClick={() => sendToTech(r.id)}
                          className="flex items-center justify-center gap-1 bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 text-xs font-bold py-2 rounded-lg hover:bg-cyan-500/30 disabled:opacity-50"
                        >
                          <Send size={14} /> {isEs ? 'Enviar a Tecnico' : 'Send to Technical'}
                        </button>
                      </div>
                    )}
                  </div>
                  {r.child_task_id && (
                    <p className="text-xs text-pulse-muted mt-2">
                      {isEs ? 'Tarea vinculada:' : 'Linked task:'} {r.child_task_id.slice(0, 8)}…
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <section>
          <h2 className="font-heading font-bold text-lg mb-3">{isEs ? 'Tareas tecnicas (seguimiento)' : 'Technical tasks'}</h2>
          <ul className="space-y-3">
            {tasks.length === 0 && <li className="text-pulse-muted text-sm">{isEs ? 'Ninguna tarea aun.' : 'No tasks yet.'}</li>}
            {tasks.map((t) => {
              const p = t.payload || {};
              return (
                <li key={t.id} className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-4 text-sm">
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <span className="text-cyan-300 font-mono text-xs">{t.public_code}</span>
                      <span className="ml-2 text-pulse-muted">{t.status}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {['IN_PROGRESS', 'READY_FOR_QA', 'CLOSED'].map((st) => (
                        <button
                          type="button"
                          key={st}
                          onClick={() => patchTask(t.id, st)}
                          disabled={saving}
                          className="text-[10px] px-2 py-1 rounded border border-pulse-border text-pulse-muted hover:text-white"
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-pulse-muted text-xs mt-2">{p.summary}</p>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
}
